/* Movatic WMS — Frontend | CesarFilho 932048016
   RootLayout.tsx
   ---------------------------------------------------------------------------
   Objetivo:
   - Header “congelado” (sticky) dentro do container rolável do conteúdo.
   - Sidebar com scroll independente do conteúdo central.
   - Mantém branding (logos Movatic/Cliente), botão “Coletor de dados”,
     e ThemeToggle. (O seletor de idioma vive agora em /settings.)

   Requisitos:
   - Utilitários no index.css: .app-shell/.app-row/.app-sidebar/.app-content/
     .app-header/.app-container (já enviados).

   Observações:
   - O header fica DENTRO de <main className="app-content"> para “grudar”
     ao topo desse container (não do viewport inteiro).
   - O overflow da sidebar é controlado no <aside className="app-sidebar">.
   --------------------------------------------------------------------------- */

import { Outlet } from 'react-router-dom'
import SidebarNav from '@/components/SidebarNav'
import ThemeToggle from '@/components/ThemeToggle'
import { useTranslation } from 'react-i18next'
import { getBranding } from '@/lib/branding'
import { Smartphone } from 'lucide-react'
import { useNavStore } from '@/store/navStore'
import { useEffect, useState } from 'react'
import CommandPalette from '@/components/CommandPalette'
import { fetchMe } from '@/lib/auth'
import { useAuthStore } from '@/store/authStore'

export default function RootLayout() {
  const { t } = useTranslation()
  const { sidebarCollapsed } = useNavStore()
  // No futuro, podemos passar user para personalizar o branding: getBranding(user)
  const { movatic: _movatic, client: _client } = getBranding()

  const [paletteOpen, setPaletteOpen] = useState(false)

  // ADR-0036: boot-load do user. Os tokens sobrevivem ao refresh mas o user
  // (em memoria) nao -> sem isto, apos F5 o tenant fica "Demo Tenant"/vazio.
  const user = useAuthStore((s) => s.user)
  const accessToken = useAuthStore((s) => s.accessToken)
  const setUser = useAuthStore((s) => s.setUser)
  useEffect(() => {
    if (accessToken && !user) {
      fetchMe()
        .then((me) =>
          setUser({
            id: (me as any)?.id ?? 0,
            username: (me as any)?.username ?? '',
            ...(me as any),
          } as any),
        )
        .catch(() => {
          /* 401/erro: o fluxo normal de auth/refresh trata; nao forcamos logout */
        })
    }
  }, [accessToken, user, setUser])
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        setPaletteOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="app-shell">
      {/* Linha principal: sidebar + conteúdo (min-h-0 é CRÍTICO para overflow funcionar) */}
      <div className="app-row">
        {/* Sidebar com scroll independente (o aside está no layout, o conteúdo no componente SidebarNav) */}
        <aside
          className="app-sidebar"
          style={{
            width: sidebarCollapsed ? '4rem' : '16rem',
            transition: 'width 0.2s ease-in-out',
          }}
        >
          <SidebarNav />
        </aside>

        {/* Conteúdo com scroll independente */}
        <main className="app-content">
          {/* ===== Header sticky (congelado no topo do main) ===== */}
          <header className="app-header" role="banner">
            <div className="mx-auto max-w-[1600px] px-4 py-3 flex items-center gap-5">
              {/* BLOCO DE LOGOS */}
              <div className="flex items-center gap-5 min-w-0">
                {/* Placeholder de logo do cliente - PREVIEW DEMO */}
                <div
                  className="shrink-0 flex items-center gap-2 px-4 py-2 h-12 lg:h-14 rounded-lg border-2 border-dashed border-blue-400/40 dark:border-blue-500/40 bg-blue-50/30 dark:bg-blue-900/10 hover:border-blue-500/60 dark:hover:border-blue-400/60 transition-colors"
                  title="O seu logo aparecera aqui"
                  aria-label="Placeholder para logo do cliente"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-5 h-5 text-blue-500/70 dark:text-blue-400/70"
                    aria-hidden="true"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span className="flex flex-col leading-tight">
                    <span className="text-[10px] uppercase tracking-wider text-blue-500/60 dark:text-blue-400/60 font-medium">
                      Branding
                    </span>
                    <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                      Seu logo aqui
                    </span>
                  </span>
                </div>
                <span className="sr-only">Placeholder de logo do cliente</span>
              </div>

              {/* Ações à direita */}
              <div className="ml-auto flex items-center gap-3">
                {/* Botão 3D “Coletor de dados” (nova aba) */}
                <a
                  href="/m/sim"
                  target="_blank"
                  rel="noopener noreferrer"
                  title={t(
                    'mobile.openCollector',
                    'Abrir coletor de dados (nova aba)',
                  )}
                  className={[
                    // layout básico
                    'inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold',
                    // cores (azul Movatic, texto branco)
                    'bg-blue-600 text-white hover:bg-blue-500 active:bg-blue-700',
                    // relevo 3D
                    'shadow-lg shadow-blue-900/30 border-b-4 border-blue-800',
                    // efeito de “apertar”
                    'transition-all duration-150 active:translate-y-0.5 active:border-b-2 active:shadow-md',
                    // ajustes
                    'select-none',
                  ].join(' ')}
                >
                  <Smartphone size={16} className="mr-0.5 opacity-90" />
                  {t('mobile.collector', 'Coletor de dados')}
                </a>

                <ThemeToggle />
              </div>
            </div>
          </header>

          {/* ===== Área central (conteúdo das páginas) ===== */}
          <div className="app-container">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />
    </div>
  )
}
